import type { IProblem } from '@platform/core/types';
import { ApiError } from './ApiError';
import { fileNameFromDisposition } from './fileNameFromDisposition';
import type { IDownload } from './IDownload';
import type { IHttpClientOptions } from './IHttpClientOptions';
import { toQuery } from './toQuery';

/** Тонкий транспорт поверх fetch: JSON, ProblemDetails → ApiError, ETag; файли — тими самими заголовками (мова, Dev-користувач, Bearer). */
export class HttpClient {
  constructor(private readonly options: IHttpClientOptions) {}

  get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return this.send<T>('GET', path + toQuery(query));
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('POST', path, body);
  }

  patch<T>(path: string, body: unknown, ifMatch?: number): Promise<T> {
    return this.send<T>('PATCH', path, body, ifMatch !== undefined ? { 'If-Match': `"${ifMatch}"` } : undefined);
  }

  put<T>(path: string, body?: unknown, ifMatch?: number): Promise<T> {
    return this.send<T>('PUT', path, body, ifMatch !== undefined ? { 'If-Match': `"${ifMatch}"` } : undefined);
  }

  delete<T = void>(path: string): Promise<T> {
    return this.send<T>('DELETE', path);
  }

  async upload<T>(path: string, file: File): Promise<T> {
    const form = new FormData();
    form.append('file', file);
    const res = await this.request(path, { method: 'POST', body: form, headers: this.options.headers() });
    return this.parse<T>(res);
  }

  /**
   * Завантаження файлу (GET) з тими самими заголовками авторизації, що й решта запитів (звичайне посилання `<a href>` їх не передає):
   * вміст — Blob, ім'я — з Content-Disposition або `fallbackName`; відповідь з помилкою — ApiError з ProblemDetails.
   */
  async download(path: string, query: Record<string, unknown> | undefined, fallbackName: string): Promise<IDownload> {
    const res = await this.request(path + toQuery(query), { method: 'GET', headers: { Accept: '*/*', ...this.options.headers() } });
    if (!res.ok) throw this.problem(res.status, await res.text());
    return { blob: await res.blob(), fileName: fileNameFromDisposition(res.headers.get('Content-Disposition'), fallbackName) };
  }

  private async send<T>(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...this.options.headers(), ...extra };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await this.request(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    return this.parse<T>(res);
  }

  /** fetch на базовий URL; мережевий збій — ApiError зі статусом 0. */
  private async request(path: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(this.options.baseUrl + path, init);
    } catch (e) {
      throw new ApiError(0, null, e instanceof Error ? e.message : 'Мережева помилка');
    }
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!res.ok) throw this.problem(res.status, text);
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Помилка відповіді: тіло ProblemDetails (JSON), якщо воно є, інакше лише код HTTP. */
  private problem(status: number, text: string): ApiError {
    let problem: IProblem | null = null;
    try { problem = text ? (JSON.parse(text) as IProblem) : null; } catch { problem = null; }
    return new ApiError(status, problem, `HTTP ${status}`);
  }
}
